/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTSVG} from '../../../helpers'
import {KTCard, KTCardBody, Select, InputPrice, Loading, Datepicker} from '../../../components'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  projectLookup,
  basicLookup,
  allCompanyByProjectLookup,
  companyInformationLookup,
} from '../../../redux/slices/lookup.slice'
import {paymentRequestCreate} from '../../../redux/slices/paymentrequest.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  PaymentRequestType: state.lookup.PaymentRequestType,
  allCompanyByProjectLookupList: state.lookup.allCompanyByProjectLookupList,
  companyInformationLookupList: state.lookup.companyInformationLookupList,
  loading: state.lookup.loading
})

const selectPaymentRequestData = (state) => ({
  isRedirect: state.paymentrequest.isRedirect,
  loading: state.paymentrequest.loading
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const {
    projectLookupList,
    PaymentRequestType,
    allCompanyByProjectLookupList,
    companyInformationLookupList,
    loading,
  } = useSelector(selectLookupData)
  const {isRedirect, loading: createLoading} = useSelector(selectPaymentRequestData)
  const dispatch = useDispatch()
  const [expenseInvoice, setExpenseInvoice] = useState([])

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(basicLookup('PaymentRequestType'))
  }, [dispatch])

  const validSchema = useMemoizedValidationSchema(() =>
    Yup.object().shape({
      ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      CompanyTitle: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      CompanyBalance: Yup.number().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      Iban: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      RequestedPayment: Yup.number().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      RequestedDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      PaymentRequestTypeId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    }), [intl]
  )

  const formValues = useMemoizedFormValues({
    ProjectId: '',
    CompanyId: '',
    ExpenseInvoiceId: null,
    Name: '',
    CompanyTitle: '',
    CompanyBalance: 0,
    IdentityNumber: '',
    TaxNumber: '',
    Iban: '',
    RequestedPayment: 0,
    RequestedDate: new Date(),
    PaymentRequestTypeId: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(paymentRequestCreate(values))
    resetForm()
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const handleRedirect = useCallback(() => {
    if (isRedirect && isRedirect.id) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.id}`)
    }
  }, [isRedirect, navigate, pageUrl])

  useEffect(() => {
    handleRedirect()
  }, [handleRedirect])

  useEffect(() => {
    if (formik.values.ProjectId)
      dispatch(allCompanyByProjectLookup({ProjectId: formik.values.ProjectId}))
  }, [formik.values.ProjectId, dispatch])

  useEffect(() => {
    if (formik.values.CompanyId)
      dispatch(companyInformationLookup({CompanyId: formik.values.CompanyId}))
  }, [formik.values.CompanyId, dispatch])

  useEffect(() => {
    if (companyInformationLookupList) {
      formik.setFieldValue('CompanyTitle', companyInformationLookupList && companyInformationLookupList.companyTitle)
      formik.setFieldValue('CompanyBalance', companyInformationLookupList && companyInformationLookupList.balance)
      formik.setFieldValue('IdentityNumber', companyInformationLookupList && companyInformationLookupList.identityNumber)
      formik.setFieldValue('TaxNumber', companyInformationLookupList && companyInformationLookupList.taxNumber)
      formik.setFieldValue('Iban', companyInformationLookupList && companyInformationLookupList.iban)
      setExpenseInvoice(companyInformationLookupList && companyInformationLookupList.expenseInvoices)
    }
  }, [companyInformationLookupList])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.PAYMENTREQUEST'}),
      path: '/expenses/payment-request',
      isSeparator: false,
      isActive: false,
    },
  ], [intl])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.PAYMENTREQUEST.ADD'})}
      </PageTitle>
      <KTCard>
        <KTCardBody>
          <form className='form' onSubmit={formik.handleSubmit} noValidate>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.PROJECTID'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={projectLookupList}
                  required
                  {...formik.getFieldProps('ProjectId')}
                  onChange={(item) => formik.setFieldValue('ProjectId', Number(item && item.value))}
                />
                {formik.touched.ProjectId && formik.errors.ProjectId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.ProjectId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.COMPANYID'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={allCompanyByProjectLookupList}
                  required
                  {...formik.getFieldProps('CompanyId')}
                  onChange={(item) => formik.setFieldValue('CompanyId', Number(item && item.value))}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.EXPENSEINVOICEID'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={expenseInvoice}
                  required
                  {...formik.getFieldProps('ExpenseInvoiceId')}
                  onChange={(item) => formik.setFieldValue('ExpenseInvoiceId', Number(item && item.value))}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.NAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  required
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Name')}
                />
                {formik.touched.Name && formik.errors.Name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.Name}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.COMPANYTITLE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  required
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('CompanyTitle')}
                />
                {formik.touched.CompanyTitle && formik.errors.CompanyTitle && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.CompanyTitle}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.COMPANYBALANCE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <InputPrice
                  {...formik.getFieldProps('CompanyBalance')}
                  onChange={(value) => formik.setFieldValue('CompanyBalance', Number(value))}
                />
                {formik.touched.CompanyBalance && formik.errors.CompanyBalance && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.CompanyBalance}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.IDENTITYNUMBER'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  maxLength={11}
                  {...formik.getFieldProps('IdentityNumber')}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.TAXNUMBER'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('TaxNumber')}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.IBAN'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Iban')}
                />

                {formik.touched.Iban && formik.errors.Iban && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.Iban}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.REQUESTEDPAYMENT'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <InputPrice
                  {...formik.getFieldProps('RequestedPayment')}
                  onChange={(value) => formik.setFieldValue('RequestedPayment', Number(value))}
                />

                {formik.touched.RequestedPayment && formik.errors.RequestedPayment && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.RequestedPayment}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.REQUESTEDATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Datepicker
                  {...formik.getFieldProps('RequestedDate')}
                  onChange={([date]) => formik.setFieldValue('RequestedDate', date)}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'PAYMENTREQUEST.CREATE.PAYMENTREQUESTTYPEID'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={PaymentRequestType}
                  required
                  {...formik.getFieldProps('PaymentRequestTypeId')}
                  onChange={(item) =>
                    formik.setFieldValue('PaymentRequestTypeId', Number(item && item.value))
                  }
                />

                {formik.touched.PaymentRequestTypeId && formik.errors.PaymentRequestTypeId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.PaymentRequestTypeId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row pt-15'>
              <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                <button
                  className='btn btn-light'
                  onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                >
                  {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  data-kt-users-modal-action='submit'
                  disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                >
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                  </span>
                  {formik.isSubmitting && (
                    <span className='indicator-progress'>
                      {intl.formatMessage({id: 'PLEASE'})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </KTCardBody>
      </KTCard>

      {(loading | createLoading) > 0 && <Loading />}
    </>
  )
}
export {Create}
